import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  FullResultsData,
  ResultService,
  UserData,
} from '../services/result.service';
import { CohortAverages } from './results-placeholders';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { RtlService } from '../services/rtl.service';
import { loaderComponent } from '../shared/components/loader/loader.component';
import { BrandService } from '../../services/brand-config.service';
import { SessionService } from '../services/session.service';
import { PlatformService } from '../services/platform.service';
import { hasDailyLoop } from '../services/daily-loop';
import { MappingTelemetryService } from '../services/mapping-telemetry.service';

import { ResultsVM } from './results-vm';
import { ResultsVmService } from './results-vm.service';
import { RoutineStoreService } from './tools/routine-store.service';
import { IntroComponent } from './panels/intro/intro.component';
import { ImprintPanelComponent } from './panels/imprint/imprint-panel.component';
import { SymptomsPanelComponent } from './panels/symptoms/symptoms-panel.component';
import { SensePanelComponent } from './panels/sense/sense-panel.component';
import { ThinkPanelComponent } from './panels/think/think-panel.component';
import { RoutinePanelComponent } from './panels/routine/routine-panel.component';
import { PaywallSheetComponent } from './ui/paywall-sheet/paywall-sheet.component';

/** Order of panels in the carousel / desktop sidebar. */
interface SidebarItem {
  panelId: string;
  labelKey: string;
  ltr?: boolean;
  score?: number | null;
  dot: string;
  scoreColor?: string;
}

const TONE_DOT: Record<string, string> = {
  green: '#1a7a4a',
  teal: '#1a7a4a',
  amber: '#f59d00',
  amberDeep: '#C47A08',
  red: '#e05a6e',
  neutral: '#c8c8cc',
};

/** URL `?panel=` slug ⇄ internal panel id. Intro carries no slug (clean URL).
 *  Slugs match the menu labels so a shared/refreshed link is human-readable. */
const PANEL_SLUG: Record<string, string> = {
  'paid-1': 'imprint',
  'paid-2': 'symptoms',
  'paid-3': 'think',
  'paid-s': 'sense',
  'paid-0': 'routine',
};
const SLUG_PANEL: Record<string, string> = Object.fromEntries(
  Object.entries(PANEL_SLUG).map(([id, slug]) => [slug, id])
);

@Component({
  selector: 'app-results-tabs',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    loaderComponent,
    IntroComponent,
    ImprintPanelComponent,
    SymptomsPanelComponent,
    SensePanelComponent,
    ThinkPanelComponent,
    RoutinePanelComponent,
    PaywallSheetComponent,
  ],
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.scss', './results-theme.css'],
  encapsulation: ViewEncapsulation.None,
  // One routine list shared by every descendant panel (the per-domain tool
  // matrices + the routine page), re-seeded each time the results screen mounts.
  providers: [RoutineStoreService],
})
export class ResultsComponent implements OnInit, OnDestroy {
  scanId = '';
  loading = true;
  /** B7: results load failed -> the loader shows the reason + Retry. */
  loadFailed = false;
  error = '';

  vm: ResultsVM | null = null;
  activePanel = 'paid-intro';

  /** Paywall v2 (locked-flow spec §1): results paywall modal visibility. */
  showPaywall = false;

  /** True when the paywall is shown over a LOCKED repeat scan with no loaded
   *  results (the 402 gate, N57-10). Forces variant B and routes home on
   *  dismiss so the locked path replaces the old blurred-teaser dead-end. */
  lockedNoData = false;

  /** After leaving a locked paywall without paying: soft "we saved your scan"
   *  confirmation instead of a dead-end (pending-locked spec §Paywall exit). */
  savedConfirm = false;

  /** carousel dots — panel ids currently mounted (grows as panels are built). */
  /** Surface split B4: the routine panel (paid-0) exists only in the app. */
  readonly isNative = inject(PlatformService).isNative;
  /** Peritale has no app, so its web keeps the routine panel (Danna 27.09). */
  readonly dailyLoop = hasDailyLoop();
  readonly dotPanels = ['paid-intro', 'paid-1', 'paid-2', 'paid-3', 'paid-s', 'paid-0'].filter(
    (p) => this.dailyLoop || p !== 'paid-0',
  );

  private sub = new Subscription();
  private destroyed = false;
  private detachCarousel?: () => void;
  private carouselEl?: HTMLElement;
  private panelRO?: ResizeObserver;
  private readonly onWinResize = () => this.syncCarouselHeight();
  /** set while goPanel() runs a programmatic smooth-scroll, so the scroll
   *  listener doesn't briefly flip the active dot to a panel we pass over. */
  private programmaticScroll = false;
  private programmaticTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private resultService: ResultService,
    private translationService: TranslationService,
    private vmService: ResultsVmService,
    public rtlService: RtlService,
    public brand: BrandService,
    private zone: NgZone,
    private session: SessionService,
    private telemetry: MappingTelemetryService
  ) {}

  async ngOnInit(): Promise<void> {
    this.scanId = this.route.snapshot.params['scanId'];
    // section 43/44: the report is the end of the funnel and the start of
    // post-mapping exploration, which is a DIFFERENT clock from the mapping.
    this.telemetry.setAssessment(this.scanId);
    this.telemetry.completeMapping();
    this.telemetry.step('report');
    this.telemetry.track('report_viewed');
    // Restore the open panel from the URL so a refresh lands back on it
    // instead of bouncing to the intro. Ignore unknown slugs.
    let panelId = SLUG_PANEL[this.route.snapshot.queryParams['panel']];
    if (panelId && !this.dotPanels.includes(panelId)) panelId = undefined as any;
    if (panelId) {
      this.activePanel = panelId;
    }
    await this.loadData();
  }

  /** The carousel only exists once data has loaded (it's behind an *ngIf), and a
   *  slow first load can take well over 10s. Attach the swipe/height sync the
   *  moment Angular renders it (and re-attach if it is ever re-created), instead
   *  of polling a fixed number of frames: when the poll gave up, the height was
   *  never pinned and short panels left a big empty space above the dots. */
  @ViewChild('sectionCarousel')
  set sectionCarouselRef(ref: ElementRef<HTMLElement> | undefined) {
    const carousel = ref?.nativeElement;
    if (carousel === this.carouselEl) return;
    this.detachCarousel?.();
    this.detachCarousel = undefined;
    this.carouselEl = undefined;
    if (!carousel) return;
    this.zone.runOutsideAngular(() => {
      this.attachCarouselSync(carousel);
      // Restored a non-intro panel from the URL → jump the carousel to it
      // (instant, no animation on first paint).
      requestAnimationFrame(() => {
        if (this.carouselEl !== carousel || this.activePanel === 'paid-intro') return;
        const el = document.getElementById(this.activePanel);
        if (el && el.parentElement === carousel) {
          this.programmaticScroll = true;
          el.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
          setTimeout(() => (this.programmaticScroll = false), 200);
        }
        this.syncCarouselHeight();
      });
    });
  }

  /** Listen for carousel scroll and drive `activePanel` (and thus the dots). */
  private attachCarouselSync(carousel: HTMLElement): void {
    this.carouselEl = carousel;
    let raf = 0;
    // Re-pinning the carousel height MUST happen only once a swipe has come to
    // rest — never mid-momentum. With `scroll-snap-type: x mandatory`, changing
    // the height during an iOS momentum scroll re-snaps every frame and strands
    // the carousel halfway between two panels (the "stuck halfway" bug). So the
    // scroll handler only updates the active dot; the height is deferred to
    // `scrollend` (with a timer fallback for browsers/iOS without that event).
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    // At rest, resolve the active panel first: a jump (no momentum frames) can
    // fire scrollend before the rAF above has updated it, which pinned the
    // height to the PREVIOUS panel and left a blank gap or clipped content.
    const settle = () => {
      if (!this.programmaticScroll) this.syncActiveFromScroll(carousel);
      this.syncCarouselHeight();
    };
    const onSettle = () => {
      settleTimer = undefined;
      settle();
    };
    const onScroll = () => {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettle, 140);
      if (raf || this.programmaticScroll) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        this.syncActiveFromScroll(carousel);
      });
    };
    const onScrollEnd = () => {
      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = undefined;
      }
      settle();
    };
    carousel.addEventListener('scroll', onScroll, { passive: true });
    carousel.addEventListener('scrollend', onScrollEnd);

    // Mobile: the carousel is a flex row, so its height stretches to the tallest
    // panel — leaving a big gap below short panels before the dots. Pin its
    // height to the active panel instead, and re-measure when a panel's content
    // changes (e.g. an accordion opening/closing inside it).
    if (typeof ResizeObserver !== 'undefined') {
      this.panelRO = new ResizeObserver(() => this.syncCarouselHeight());
      for (const panel of Array.from(carousel.children)) {
        this.panelRO.observe(panel as HTMLElement);
      }
    }
    window.addEventListener('resize', this.onWinResize);
    this.syncCarouselHeight();

    this.detachCarousel = () => {
      carousel.removeEventListener('scroll', onScroll);
      carousel.removeEventListener('scrollend', onScrollEnd);
      window.removeEventListener('resize', this.onWinResize);
      this.panelRO?.disconnect();
      if (raf) cancelAnimationFrame(raf);
      if (settleTimer) clearTimeout(settleTimer);
    };
  }

  /** Pin the carousel height to the active panel (mobile only). */
  private syncCarouselHeight(): void {
    const carousel = this.carouselEl;
    if (!carousel) return;
    // Desktop shows one panel via CSS; let it size naturally.
    if (window.innerWidth >= 1080) {
      carousel.style.height = '';
      return;
    }
    const active = document.getElementById(this.activePanel);
    if (active && active.parentElement === carousel) {
      carousel.style.height = active.offsetHeight + 'px';
    }
  }

  /** Pick the panel whose centre is nearest the carousel centre. */
  private syncActiveFromScroll(carousel: HTMLElement): void {
    const cRect = carousel.getBoundingClientRect();
    const center = cRect.left + cRect.width / 2;
    let bestId = '';
    let bestDist = Infinity;
    for (const panel of Array.from(carousel.children) as HTMLElement[]) {
      if (!panel.id) continue;
      const r = panel.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - center);
      if (dist < bestDist) {
        bestDist = dist;
        bestId = panel.id;
      }
    }
    if (bestId && bestId !== this.activePanel) {
      // Update the dot only — height is re-pinned on scroll settle (see
      // attachCarouselSync), never mid-scroll, to avoid the iOS snap interrupt.
      this.zone.run(() => {
        this.activePanel = bestId;
        this.syncPanelUrl(bestId);
      });
    }
  }

  /** Reflect the active panel in the URL (`?panel=<slug>`) so a refresh
   *  restores it. Uses Location.replaceState — a pure history swap that does
   *  NOT run the router, so the async authGuard isn't re-fired on every
   *  swipe/click (which otherwise queued navigations and froze panel switching
   *  for seconds). The query is still parsed on a real reload. */
  private syncPanelUrl(panelId: string): void {
    const path = this.router.url.split('?')[0];
    const slug = PANEL_SLUG[panelId];
    this.location.replaceState(path, slug ? `panel=${slug}` : '');
  }

  /** B7: Retry from the failed loader re-runs the same load. */
  retryLoad(): void {
    this.error = '';
    void this.loadData();
  }

  private async loadData(): Promise<void> {
    // Coming back to the same scan (nav tab, back from a tool or the report):
    // paint the last results at once and confirm them quietly in the
    // background, instead of the full loader every time.
    const snap = this.resultService.getResultsSnapshot(this.scanId);
    if (snap) {
      this.render(snap.full, snap.userData, snap.cohort);
      this.loading = false;
      this.loadFailed = false;
      void this.refreshInBackground();
      return;
    }
    this.loading = true;
    this.loadFailed = false;
    try {
      // A guest only ever reaches results for the seeded dev/QA scan (authGuard
      // gates every real scan to signup). A guest has no session, so the
      // user-scoped history + cohort calls would 401 and force-log-out — skip
      // them. The VM already prefers the per-scan modules carried on `full`, so
      // results still render (just without cross-scan history/cohort).
      const hasSession = !!this.session.session?.token;
      const [full, userData] = await Promise.all([
        this.resultService.getFullResults(this.scanId),
        hasSession
          ? this.resultService.getUserData(this.scanId).catch(() => undefined)
          : Promise.resolve(undefined),
      ]);
      if (!full) {
        this.error = this.translationService.translate('results.errors.failedToLoad');
        this.loadFailed = true;
        return;
      }
      const age = Number((full as FullResultsData)?.userInfo?.user?.age) || 0;
      // Cohort averages are a non-essential comparison rail; the VM renders fine
      // with null. Never let a transient cohort fetch failure blank the whole
      // results page ("Failed to load results data").
      const cohort = hasSession
        ? await this.resultService.getCohortAverages(age).catch(() => null)
        : null;
      this.render(full as FullResultsData, (userData as UserData) ?? null, cohort);
    } catch (error: any) {
      if (error?.status === 402) {
        // Locked repeat scan (not entitled, no credit). Show paywall v2
        // DIRECTLY over this route instead of the old blurred-teaser screen
        // (/app/trial-result) — N57-10. vm stays null; the sheet is a
        // full-screen overlay and dismiss routes home (see closePaywall) so the
        // locked path is never a dead-end.
        this.lockedNoData = true;
        this.showPaywall = true;
        return;
      }
      if (error?.status === 401) {
        this.router.navigate(['/app/login']);
        return;
      }
      this.error = this.translationService.translate(
        error?.status === 404
          ? 'results.errors.scanNotFound'
          : 'results.errors.failedToLoad'
      );
      // B7: a load failure is retryable (nothing of hers is lost - the scan is
      // already saved), so the loader offers Retry rather than a dead end.
      this.loadFailed = true;
    } finally {
      this.loading = false;
    }
  }

  private render(
    full: FullResultsData,
    userData: UserData | null,
    cohort: CohortAverages | null
  ): void {
    this.vm = this.vmService.build(this.scanId, full, userData, cohort);
    this.buildSidebar();
    this.resultService.setResultsSnapshot({ scanId: this.scanId, full, userData, cohort });
  }

  /** Re-fetch behind a cached render. Only re-renders when something actually
   *  changed (e.g. she finished SENSE since), so open accordions don't reset.
   *  A failure keeps what is on screen; a lost entitlement shows the paywall. */
  private async refreshInBackground(): Promise<void> {
    const scanId = this.scanId;
    const hasSession = !!this.session.session?.token;
    try {
      const [full, userData] = await Promise.all([
        this.resultService.getFullResults(scanId),
        hasSession
          ? this.resultService.getUserData(scanId).catch(() => undefined)
          : Promise.resolve(undefined),
      ]);
      if (!full || this.destroyed || scanId !== this.scanId) return;
      const age = Number(full?.userInfo?.user?.age) || 0;
      const cohort = hasSession
        ? await this.resultService.getCohortAverages(age).catch(() => null)
        : null;
      if (this.destroyed) return;
      const prev = this.resultService.getResultsSnapshot(scanId);
      const next = { full, userData: (userData as UserData) ?? null, cohort };
      const changed =
        !prev ||
        JSON.stringify([prev.full, prev.userData, prev.cohort]) !==
          JSON.stringify([next.full, next.userData, next.cohort]);
      if (changed) {
        this.render(next.full, next.userData, next.cohort);
      }
    } catch (error: any) {
      if (this.destroyed) return;
      if (error?.status === 402) {
        this.vm = null;
        this.lockedNoData = true;
        this.showPaywall = true;
      }
    }
  }

  /** Desktop sidebar — the 4 scored domains (intro/routine/doctor rendered
   *  explicitly). Built ONCE per scan and stored in a stable field. A getter
   *  here returned a fresh array every change-detection, so the `*ngFor` tore
   *  down and rebuilt every nav <button> on each CD tick — a rapid second click
   *  would land on a button an intervening CD had just destroyed, swallowing the
   *  click ("menu doesn't respond until you keep clicking"). */
  sidebar: SidebarItem[] = [];

  private buildSidebar(): void {
    this.sidebar = (this.vm?.domains ?? [])
      .filter((d) => d.id !== 'routine')
      .map((d) => ({
        panelId: d.panelId,
        labelKey: d.titleKey,
        ltr: d.titleLtr,
        score: d.score,
        dot: d.band ? TONE_DOT[d.band.tone] : '#c8c8cc',
        scoreColor: d.band ? TONE_DOT[d.band.tone] : '#888888',
      }));
  }

  /** Stable ngFor identity so Angular reuses the same <button> across CD. */
  trackByPanelId = (_: number, item: SidebarItem): string => item.panelId;
  trackByDot = (_: number, id: string): string => id;

  goPanel(panelId: string): void {
    this.activePanel = panelId;
    this.syncPanelUrl(panelId);
    // Mobile: scroll the carousel to the panel. Desktop: CSS shows only .active.
    const el = document.getElementById(panelId);
    if (el) {
      // Suppress scroll-driven dot updates while the smooth-scroll plays out,
      // otherwise the dot flickers through every panel we glide past.
      this.programmaticScroll = true;
      if (this.programmaticTimer) clearTimeout(this.programmaticTimer);
      this.programmaticTimer = setTimeout(() => (this.programmaticScroll = false), 600);
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    this.syncCarouselHeight();
  }

  goDoctorReport(): void {
    // paid-4 doctor summary is locked for NON-ENTITLED accounts → paywall, not
    // report. Entitled = paid OR admin OR admin-granted credit, so admins pass.
    if (this.vm && !this.vm.isEntitled) {
      this.openPaywall();
      return;
    }
    this.router.navigate(['/app/doctor-report', this.scanId]);
  }

  /** SENSE empty-state CTA (handoff 03 sec ו): start the vision+hearing check for this scan. */
  startSenseCheck(): void {
    this.router.navigate(['/app/sense', this.scanId]);
  }

  // --- Paywall v2 locks (locked-flow spec §1) ---
  get isPaid(): boolean {
    return !!this.vm?.isEntitled;
  }

  /** Option A: the routine (paid-0) is sold; locked for non-entitled. */
  get routineLocked(): boolean {
    return !!this.vm && !this.vm.isEntitled && !!this.vm.toolsLocked;
  }

  /** Doctor summary (paid-4) embedded in the routine panel — locked for any
   *  non-entitled account (both options). */
  get doctorLocked(): boolean {
    return !!this.vm && !this.vm.isEntitled;
  }

  openPaywall(): void {
    this.showPaywall = true;
  }

  /** v2 paywall variant: A on the first check (a free "see my results" path
   *  stays), B on a returning scan (full gate, quiet exit link). A locked 402
   *  is by definition a non-first, non-entitled scan → always B. */
  get paywallVariant(): 'A' | 'B' {
    if (this.lockedNoData) return 'B';
    return (this.vm?.checkCount ?? 1) >= 2 ? 'B' : 'A';
  }

  closePaywall(): void {
    this.showPaywall = false;
    // Locked scan → nothing rendered behind the sheet, so don't dead-end. Show
    // the soft "we saved your scan" confirmation (the scan is already persisted
    // server-side as completed-locked); she routes home from there and can
    // reopen it later from the profile pending list (pending-locked spec).
    if (this.lockedNoData) {
      this.savedConfirm = true;
    }
  }

  /** In-app store purchase granted access: drop the paywall and re-render as
   *  entitled (the VM reads entitlement from the refreshed session). A locked
   *  scan had no data yet, so it loads fresh. */
  onPurchased(): void {
    this.showPaywall = false;
    this.savedConfirm = false;
    const snap = this.resultService.getResultsSnapshot(this.scanId);
    if (snap && !this.lockedNoData) {
      this.render(snap.full, snap.userData, snap.cohort);
      void this.refreshInBackground();
      return;
    }
    this.lockedNoData = false;
    void this.loadData();
  }

  /** Leave the "saved" confirmation → back home / initial review. */
  leaveSaved(): void {
    this.savedConfirm = false;
    this.lockedNoData = false;
    this.router.navigate(['/app/home']);
  }

  /** Modal CTA → the consumer paywall screen, carrying the scan as context. */
  goToPayment(): void {
    this.showPaywall = false;
    const lang = this.translationService.getCurrentLanguage();
    this.router.navigate(['/' + lang + '/app/payment'], {
      queryParams: { scanId: this.scanId || undefined },
    });
  }

  get isRtl(): boolean {
    return this.translationService.isRTL();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.sub.unsubscribe();
    this.detachCarousel?.();
    if (this.programmaticTimer) clearTimeout(this.programmaticTimer);
  }
}
